"use client";

import { useEffect, useRef, type ReactNode } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Hero from "./Hero";

/**
 * Pins the Hero while `children`, which sit right after it in normal document
 * flow, scroll up over it. The children are never transformed: they move at
 * native 1:1 scroll speed, so the cover starts on the first scroll tick and
 * hands off to ordinary scrolling with no change in speed. pinSpacing is off
 * because the overlap is the effect — no extra scroll distance is added.
 */
export default function HeroCoverTransition({ children }: { children: ReactNode }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const wrapper = wrapperRef.current!;
      const hero = heroRef.current!;

      ScrollTrigger.create({
        trigger: hero,
        // Pin from the first pixel of scroll, at the hero's resting position below the fixed navbar
        // ("top top" would let it scroll 80px under the navbar first). A hero taller than the space
        // left (phones) instead pins once its bottom is on screen, so its CTA is seen before being covered.
        start: () => {
          // Measured from the wrapper, not the hero, which is inside the pin while this runs.
          const restingTop = wrapper.getBoundingClientRect().top + window.scrollY;
          return hero.offsetHeight <= window.innerHeight - restingTop ? `top ${restingTop}px` : "bottom bottom";
        },
        // By the time the hero's bottom reaches the viewport top, the children fully cover it.
        end: "bottom top",
        pin: true,
        pinSpacing: false,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      });
    }, wrapperRef);

    const handleLoad = () => ScrollTrigger.refresh();
    window.addEventListener("load", handleLoad);

    return () => {
      window.removeEventListener("load", handleLoad);
      ctx.revert();
    };
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <div ref={heroRef} className="relative z-0">
        <Hero />
      </div>
      {/* Must be opaque, or the pinned hero shows through as it is covered. */}
      <div className="relative z-10">{children}</div>
    </div>
  );
}
